import { ApiError, apiFetch, type ApiGetToken } from "@/lib/api"

/** Mirrors `UserPublic` from the API — dates arrive as ISO strings in JSON. */
export type UserPublic = {
  id: number
  email: string
  first_name: string | null
  last_name: string | null
  avatar_url: string | null
  is_active: boolean
  /** "light" | "dark" | null (system). */
  theme: string | null
  created_at: string | null
  updated_at: string | null
  full_name: string | null
}

export type AccountPublic = {
  id: number
  name: string | null
  notifications_in_app: boolean
  created_at: string | null
  updated_at: string | null
  /** Set = account-deletion requested (FLO-185); every other API call 403s while set. */
  deletion_requested_at: string | null
  /** Permanent-purge date; null while `deletion_requested_at` is set means a legal hold ("under review") — never show a NaN day count. */
  purge_scheduled_at: string | null
}

/** Membership summary for the account switcher — mirrors backend `MeAccount`. */
export type MeAccount = {
  id: number
  name: string
  role_key: string
  /** Set = this membership's account has a pending deletion request (FLO-185) — drives the switcher "Deleting" badge. */
  deletion_requested_at: string | null
}

export type RolePublic = {
  id: number
  key: string
  name: string
  created_at: string | null
  updated_at: string | null
}

/** Entitlement snapshot from `/me` — mirrors backend `MeBilling`. */
export type MeBilling = {
  /** "comped" | "trialing" | "paid" | "free". */
  access_level: string
  is_unlimited: boolean
  /** Live (non-archived) projects right now. */
  project_count: number
  /** Plan key for the chip; null with no plan. */
  plan: "starter" | "pro" | null
  seat_count: number
  trial_ends_at: string | null
}

/** GET `/api/users/me` — session + tenant + RBAC + billing. */
export type MePublic = {
  user: UserPublic
  /** The ACTIVE account (tenant every other request resolves to). */
  account: AccountPublic
  role: RolePublic
  /** Every active membership (`id`, `name`, `role_key`) — drives the account switcher. */
  accounts: MeAccount[]
  permissions: string[]
  is_staff: boolean
  billing: MeBilling
  /** Global feature flags ({ key: enabled }) — read via `useFeatureFlag`, keys in `@/lib/feature-flags`. */
  feature_flags: Record<string, boolean>
}

export async function fetchMe(getToken: ApiGetToken): Promise<MePublic> {
  return apiFetch<MePublic>("/api/users/me", {
    method: "GET",
    getToken,
  })
}

/**
 * POST `/api/accounts/switch` — persists `last_active_account_id` server-side (204). Best-effort
 * from the switcher: `keepalive` lets the request survive the full-page navigation that follows,
 * and a failure only costs the stored preference (the `X-Account-Id` header still selects).
 */
export async function switchAccount(accountId: number, getToken: ApiGetToken): Promise<void> {
  await apiFetch<undefined>("/api/accounts/switch", {
    method: "POST",
    body: JSON.stringify({ account_id: accountId }),
    keepalive: true,
    getToken,
  })
}

/** Structured `detail` shape `resolve_account` raises for the deactivated-membership 403 (`app/deps.py`). */
type StructuredErrorDetail = { code: string; message: string }

/** `detail.code` from a structured `ApiError` with the given status, else `null`. */
function structuredErrorCode(error: unknown, status: number): string | null {
  if (!(error instanceof ApiError) || error.status !== status) return null
  const detail = (error.data as { detail?: unknown } | undefined)?.detail
  if (typeof detail !== "object" || detail === null) return null
  const code = (detail as Partial<StructuredErrorDetail>).code
  return typeof code === "string" ? code : null
}

/**
 * True for the 403 `resolve_account` raises when a user has no active membership anywhere
 * (FLO-166; widened by FLO-192 from "this account" to "everywhere") — a stable app state (not a
 * transient failure), so callers use this to skip retries and show a dedicated screen instead of
 * the generic error/loading states.
 */
export function isMembershipInactiveError(error: unknown): boolean {
  return structuredErrorCode(error, 403) === "membership_inactive"
}

/**
 * True for the 403 every non-`/me` endpoint returns while the active account has a pending
 * deletion request (FLO-185) — `{ code: "account_pending_deletion", message }`. Like
 * `isMembershipInactiveError`, this is a stable app state (not a transient failure): callers skip
 * retries/Sentry and `AuthenticatedLayout` renders `AccountPendingDeletionScreen` instead. `/me`
 * itself is exempt from the lockout, so this never surfaces from `useMeQuery` — only from other
 * in-flight queries/mutations racing the lockout taking effect.
 */
export function isAccountPendingDeletionError(error: unknown): boolean {
  return structuredErrorCode(error, 403) === "account_pending_deletion"
}

